import { CheckBox } from "@portal/BaseComponents/CheckBox/AntCheckBox";
import { onCheckBoxChange } from "@portal/BaseComponents/CheckBox/type";
import { DocUploader } from "@portal/BaseComponents/Document/DocUpload/DocUploader";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import {
  Form,
  useForm,
  useFormWatch,
} from "@portal/BaseComponents/Form/AntForm";
import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { InvoiceLineItems } from "@portal/BaseComponents/InvoiceLineItems/InvoiceLineItems";
import { LineItemProps } from "@portal/BaseComponents/InvoiceLineItems/type";
import { InvoiceSearchWrapper } from "@portal/BaseComponents/InvoicesSearch/InvoiceSearchWrapper";
import { onSelectedValuesChange } from "@portal/BaseComponents/InvoicesSearch/type";
import { Modal } from "@portal/BaseComponents/Modal/AntModal";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { FormFooter } from "@portal/BaseComponents/UI/FormArea/FormFooter";
import { FormHeader } from "@portal/BaseComponents/UI/FormArea/FormHeader";
import { Icon } from "@portal/lib/icons/icons";
import { useRaiseADisputeQuery } from "@portal/queries/dispute-query";
import {
  CreateDispute,
  CreateDisputeForm,
  disputeFiledIds,
} from "@portal/types/entities/dispute";
import { Invoice } from "@portal/types/entities/invoice";
import { head } from "lodash";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DisputeDescription,
  DisputeTitle,
  DisputeTypesForm,
} from "../DisputeFormComponents";
import CustomFieldFormItem from "../CustomFieldEditor/CustomFieldFormItem";

const RDIcon = (
  <Icon
    icon={["far", "comment-times"]}
    color="var(--orange-6)"
    size="2x"
    width="24px"
  />
);

const t = {
  lineItem: "Refer a Line Item",
};

interface DisputeModalProps {
  open: boolean;
  onClose: () => void;
  getUpdatedValues?: (value: CreateDispute) => void;
  value?: CreateDispute;
  onOpenChange?: (open: false) => void;
  defaultInvoiceValue?: Invoice[];
}
export const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

function DisputeModal(props: DisputeModalProps) {
  const {
    open,
    onClose,
    // getUpdatedValues,
    value,
    onOpenChange,
    defaultInvoiceValue,
  } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLineItem, setIsLineItem] = useState<
    Partial<LineItemProps> | undefined
  >({
    isLineItem: defaultInvoiceValue?.length === 1,
    title: head(defaultInvoiceValue)?.number,
  });
  const [form] = useForm<CreateDisputeForm>();
  const handleRaiseADispute = useRaiseADisputeQuery();
  const invoice_ids = useFormWatch([disputeFiledIds.invoice_ids], form);

  function handleClose() {
    onClose();
    setIsLineItem(undefined);
    if (searchParams.has("entity_type")) {
      setSearchParams();
    }
  }
  function createDispute(value: CreateDispute) {
    handleRaiseADispute.mutateAsync(value).then(() => {
      handleClose();
    });
  }
  function handleSubmit() {
    form.validateFields().then(() => {
      const values = form.getFieldsValue();
      if (values.file_upload_ids) {
        const uploadIds = values.file_upload_ids.map(
          (f) => f.response?.id
        ) as string[];
        createDispute({ ...values, file_upload_ids: uploadIds });
      } else {
        createDispute(values as CreateDispute);
      }
    });
  }

  function handleInvoiceSearch(...args: onSelectedValuesChange) {
    const [value, option] = args;
    form.setFieldValue(disputeFiledIds.invoice_ids, value);
    setIsLineItem((previousValues) => {
      return {
        ...previousValues,
        isLineItem: value.length === 1,
        title: option?.length ? option?.[0].number : undefined,
        refer: previousValues?.refer
          ? value.length === 1
          : previousValues?.refer,
      };
    });

    if (!value.length) {
      form.setFieldValue(disputeFiledIds.invoice_line_item_ids, []);
    }
  }

  function handleReferItemChange(event: onCheckBoxChange[number]) {
    const updatePredicate = (
      previousValues: Partial<LineItemProps> | undefined
    ) => {
      return { ...previousValues, refer: event.target.checked };
    };
    setIsLineItem(updatePredicate);
    if (!event.target.checked) {
      form.setFieldValue(disputeFiledIds.invoice_line_item_ids, []);
    }
  }

  function handleOpenChange() {
    handleClose();
    onOpenChange?.(false);
  }

  const HeaderTitle = (
    <Flex align="center" justify="start">
      {RDIcon}
      <TypographyText fs="20">Raise a Dispute</TypographyText>
    </Flex>
  );

  const Header = (
    <FormHeader title={HeaderTitle} onClose={handleClose} mode="drawer" />
  );

  const Footer = (
    <FormFooter
      cancelButtonProps={{ onClick: handleClose }}
      saveButtonProps={{
        onClick: handleSubmit,
        loading: handleRaiseADispute.isLoading,
      }}
    />
  );

  const AssociatedInvoice = (
    <StyledFormItem
      label="Add Invoices"
      required
      initialValue={
        value?.invoice_ids || defaultInvoiceValue?.map((invoice) => invoice.id)
      }
      style={{ width: "100%" }}
      preserve={false}
      name={disputeFiledIds.invoice_ids}
      rules={[{ required: true, message: "This Field is Mandatory" }]}
    >
      <InvoiceSearchWrapper
        selectedValuesCallBack={handleInvoiceSearch}
        initialValue={defaultInvoiceValue}
        openInvoicesOnly
      />
    </StyledFormItem>
  );

  const DisputeTypes = (
    <StyledFormItem
      label="Dispute Type"
      space="sp-1"
      required
      preserve={false}
      initialValue={value?.dispute_type_ids}
      name={disputeFiledIds.dispute_type_ids}
      rules={[{ required: true, message: "This Field is Mandatory" }]}
    >
      <DisputeTypesForm />
    </StyledFormItem>
  );

  const Title = (
    <StyledFormItem
      label="Title"
      space="sp-1"
      required
      preserve={false}
      initialValue={value?.title}
      name={disputeFiledIds.title}
      rules={[{ required: true, message: "This Field is Mandatory" }]}
    >
      <DisputeTitle />
    </StyledFormItem>
  );

  const Description = (
    <StyledFormItem
      label="Description"
      space="sp-1"
      preserve={false}
      name={disputeFiledIds.description}
      initialValue={value?.description}
    >
      <DisputeDescription />
    </StyledFormItem>
  );

  const Attachments = (
    <StyledFormItem
      space="sp-1"
      label="Attachments"
      preserve={false}
      name={disputeFiledIds.file_upload_ids}
      initialValue={value?.file_upload_ids}
      valuePropName="fileList"
      getValueFromEvent={normFile}
      style={{ margin: 0 }}
    >
      <DocUploader action="/api/v1/attachments" previewType="UPLOADED" />
    </StyledFormItem>
  );
  const LineItem = (
    <StyledFormItem
      label="Select Line Item of the Invoice"
      name={disputeFiledIds.invoice_line_item_ids}
      space="sp-1"
      initialValue={value?.invoice_line_item_ids}
      preserve={false}
    >
      <InvoiceLineItems
        title={isLineItem?.title}
        selectedInvoices={invoice_ids}
      />
    </StyledFormItem>
  );

  const ReferLineItem = (
    <CheckBox
      design="border-design"
      style={{ width: "200px" }}
      onChange={handleReferItemChange}
      disabled={!isLineItem?.isLineItem}
      checked={isLineItem?.refer}
    >
      {t.lineItem}
    </CheckBox>
  );

  const InvoiceArea = (
    <Flex
      align="flex-start"
      space="8"
      justify="start"
      vertical
      style={{ marginBottom: "var(--space-32)" }}
    >
      {AssociatedInvoice}
      {ReferLineItem}
    </Flex>
  );
  const CustomField = (
    <CustomFieldFormItem customFields={value?.custom_field_data} form={form} />
  );

  return (
    <Modal
      open={open}
      width={640}
      closable={false}
      centered
      title={Header}
      footer={Footer}
      destroyOnClose
      forceRender={false}
      styles={{
        body: {
          overflow: "auto",
          maxHeight: "600px",
        },
      }}
      onCancel={handleOpenChange}
    >
      <Form form={form} layout="vertical" preserve={false}>
        {InvoiceArea}
        {isLineItem?.refer ? LineItem : null}
        {DisputeTypes}
        {Title}
        {Description}
        {Attachments}
        {CustomField}
      </Form>
    </Modal>
  );
}

export default React.memo(DisputeModal);
